import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardBody, Col, Row, Spinner, Badge, Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import FilterComponent from "../../Components/Tables/DataTables/Common/FilterComponent";
import { Btn } from "../../AbstractElements";
import axios from "axios";
import { baseUrl, mediaUrl } from "../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEdit, FaTrash, FaUserTie, FaUser, FaBriefcase } from "react-icons/fa";
import { useAuth } from "../../Context/AuthContext";

interface RoleObj {
  id: number;
  name: string;
  slug: string;
}

interface EmployeeItem {
  id: number;
  name: string;
  email: string;
  phone: string;
  designation: string;
  address: string;
  joining_date: string;
  status: string;
  profile_image: string;
  role_id: number;
  role?: RoleObj;
  created_at: string;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: { style: { borderBottom: "1px solid #dee2e6" } },
  table: { style: { border: "1px solid #dee2e6", borderRadius: "8px" } },
};

export default function ViewEmployees() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [employees, setEmployees] = useState<EmployeeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modal State for Viewing Employee Details
  const [viewModal, setViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeItem | null>(null);
  const [activeTab, setActiveTab] = useState("1");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText), 500);
    return () => clearTimeout(timer);
  }, [filterText]);

  const fetchEmployees = useCallback(
    async (page = currentPage, pp = perPage) => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${baseUrl}employee/get_all`,
          {
            page,
            per_page: pp,
            search: debouncedSearch.trim() || undefined,
            sort_by: "id",
            sort_direction: "desc",
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEmployees(response.data.data || []);
        setTotalRows(response.data.total || 0);
        setCurrentPage(response.data.current_page || 1);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to fetch employees.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, debouncedSearch, token]
  );

  useEffect(() => {
    fetchEmployees(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearch]);

  const handleDelete = useCallback(
    (empId: number, empName: string) => {
      Swal.fire({
        title: "Are you sure?",
        text: `Do you really want to delete employee "${empName}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios.post(
              `${baseUrl}employee/delete`,
              { id: empId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Employee deleted successfully.");
            fetchEmployees(currentPage, perPage);
          } catch (error: any) {
            toast.error(error.response?.data?.message || "Failed to delete employee.");
          }
        }
      });
    },
    [token, currentPage, perPage, fetchEmployees]
  );

  const handleOpenViewModal = (emp: EmployeeItem) => {
    setSelectedEmployee(emp);
    setActiveTab("1");
    setViewModal(true);
  };

  const columns = useMemo(
    () => [
      {
        name: <div>Sl.No</div>,
        cell: (_: any, index: number) => (currentPage - 1) * perPage + index + 1,
        width: "75px",
      },
      {
        name: <div>Name</div>,
        cell: (row: EmployeeItem) => (
          <div className="d-flex align-items-center gap-2">
            <img
              src={
                row.profile_image
                  ? `${mediaUrl}/${row.profile_image}`
                  : "https://via.placeholder.com/40"
              }
              alt={row.name}
              onError={(e: any) => {
                e.target.src = "https://via.placeholder.com/40";
              }}
              style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover" }}
            />
            <strong style={{ color: "#308e87", fontSize: "14px" }}>{row.name}</strong>
          </div>
        ),
        sortable: true,
      },
      {
        name: <div>Email</div>,
        cell: (row: EmployeeItem) => row.email,
        sortable: true,
      },
      {
        name: <div>Phone</div>,
        cell: (row: EmployeeItem) => row.phone || "—",
      },
      {
        name: <div>Designation</div>,
        cell: (row: EmployeeItem) => row.designation || "—",
      },
      {
        name: <div>Role</div>,
        cell: (row: EmployeeItem) => (
          <Badge color="primary" pill style={{ fontSize: "12px", padding: "5px 10px" }}>
            {row.role?.name || "No Role"}
          </Badge>
        ),
      },
      {
        name: <div>Status</div>,
        cell: (row: EmployeeItem) => (
          <Badge color={row.status === "Active" ? "success" : "danger"} pill style={{ fontSize: "12px", padding: "5px 10px" }}>
            {row.status}
          </Badge>
        ),
      },
      {
        name: <div>Actions</div>,
        cell: (row: EmployeeItem) => (
          <div className="d-flex gap-2">
            {hasPermission("employee-management.view") && (
              <Btn
                color="info"
                size="sm"
                className="p-1"
                title="View Details"
                onClick={() => handleOpenViewModal(row)}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaEye size={14} />
              </Btn>
            )}
            {hasPermission("employee-management.edit") && (
              <Btn
                color="warning"
                size="sm"
                className="p-1"
                title="Edit Employee"
                onClick={() => navigate("/edit_employee", { state: { id: row.id } })}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaEdit size={14} />
              </Btn>
            )}
            {hasPermission("employee-management.delete") && (
              <Btn
                color="danger"
                size="sm"
                className="p-1"
                title="Delete Employee"
                onClick={() => handleDelete(row.id, row.name)}
                style={{ width: "30px", height: "30px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <FaTrash size={14} />
              </Btn>
            )}
          </div>
        ),
      },
    ],
    [currentPage, perPage, navigate, handleDelete, hasPermission]
  );

  return (
    <div className="page-body">
      <Breadcrumbs pageTitle="Employee Management" parent="Master Settings" title="Employees" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {hasPermission("employee-management.add") && (
              <Col xs={12} className="mt-2 d-flex justify-content-end mb-3">
                <Btn color="primary" onClick={() => navigate("/add_employee")}>
                  Add
                </Btn>
              </Col>
            )}
            {hasPermission("employee-management.view") && (
              <>
                <div className="mb-3">
                  <FilterComponent
                    onFilter={(e: any) => {
                      setFilterText(e.target.value);
                      setCurrentPage(1);
                    }}
                    filterText={filterText}
                  />
                </div>

                <div className="table-responsive custom-scrollbar">
                  <DataTable
                    data={employees}
                    columns={columns}
                    highlightOnHover
                    striped
                    customStyles={customStyles}
                    pagination
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={(p) => setCurrentPage(p)}
                    onChangeRowsPerPage={(np) => {
                      setPerPage(np);
                      setCurrentPage(1);
                    }}
                    paginationDefaultPage={currentPage}
                    responsive
                    progressPending={loading}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", width: "100%" }}>
                        <Spinner color="primary" />
                      </div>
                    }
                  />
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* View Employee Details Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg" centered>
        <ModalHeader
          toggle={() => setViewModal(false)}
          style={{ background: "linear-gradient(135deg, #308e87 0%, #1f5e59 100%)", color: "#fff" }}
        >
          <div className="d-flex align-items-center gap-2" style={{ color: "#fff" }}>
            <FaUserTie size={20} />
            <span>Employee Details: {selectedEmployee?.name}</span>
          </div>
        </ModalHeader>
        <ModalBody>
          {selectedEmployee && (
            <div>
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={activeTab === "1" ? "active font-weight-bold" : ""}
                    onClick={() => setActiveTab("1")}
                    style={{ cursor: "pointer" }}
                  >
                    <FaUser className="me-1" /> Personal Info
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "2" ? "active font-weight-bold" : ""}
                    onClick={() => setActiveTab("2")}
                    style={{ cursor: "pointer" }}
                  >
                    <FaBriefcase className="me-1" /> Work Details
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <div className="d-flex align-items-center gap-3 mb-4 p-3 bg-light rounded">
                    <img
                      src={
                        selectedEmployee.profile_image
                          ? `${mediaUrl}/${selectedEmployee.profile_image}`
                          : "https://via.placeholder.com/100"
                      }
                      alt={selectedEmployee.name}
                      onError={(e: any) => {
                        e.target.src = "https://via.placeholder.com/100";
                      }}
                      style={{ width: "90px", height: "90px", borderRadius: "50%", objectFit: "cover", border: "3px solid #308e87" }}
                    />
                    <div>
                      <h5 className="mb-1 text-primary font-weight-bold">{selectedEmployee.name}</h5>
                      <p className="mb-1 text-muted">{selectedEmployee.designation}</p>
                      <Badge color={selectedEmployee.status === "Active" ? "success" : "danger"} pill>
                        {selectedEmployee.status}
                      </Badge>
                    </div>
                  </div>

                  <Row className="gy-3">
                    <Col md={6}>
                      <strong>Email:</strong> <p className="mb-0 text-muted">{selectedEmployee.email}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Phone:</strong> <p className="mb-0 text-muted">{selectedEmployee.phone || "N/A"}</p>
                    </Col>
                    <Col md={12}>
                      <strong>Address:</strong> <p className="mb-0 text-muted">{selectedEmployee.address || "N/A"}</p>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="2">
                  <Row className="gy-3 p-2">
                    <Col md={6}>
                      <strong>Designation:</strong> <p className="mb-0 text-muted">{selectedEmployee.designation || "N/A"}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Role Assigned:</strong> <p className="mb-0 text-muted">{selectedEmployee.role?.name || "N/A"}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Status:</strong> <p className="mb-0">{selectedEmployee.status}</p>
                    </Col>
                    <Col md={6}>
                      <strong>Joining Date:</strong> <p className="mb-0 text-muted">{selectedEmployee.joining_date || "N/A"}</p>
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
            </div>
          )}
        </ModalBody>
      </Modal>

      <ToastContainer />
    </div>
  );
}
